import { AsyncPipe, DatePipe, NgClass, NgStyle, SlicePipe } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule } from '@ngx-translate/core';
import { CalendarCommonModule, CalendarMonthModule, CalendarMonthViewDay } from 'angular-calendar';
import * as moment from 'moment';
import { of, Subject } from 'rxjs';
import { DATE_TIME_ISO_T_FORMAT } from '../../../constants/date.constants';
import { WIDGET_DISPLAY_TIMEZONE_SOURCE } from '../../../constants/widget-constants';
import { TimezoneDbModel } from '../../../db-models/timezone.model';
import { UserDao } from '../../../db-models/user-data-dao';
import { Partner } from '../../../models/global';
import { UserState } from '../../../models/state.model';
import { WidgetColorConf } from '../../../models/widget-color.model';
import { CustomEventService } from '../../../services/custom-event.service';
import { TimezoneService } from '../../../services/timezone.service';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { TimezoneSelectComponent } from '../../../shared/components/timezone-select/timezone-select.component';

const modules = [CalendarCommonModule, FontAwesomeModule, CalendarMonthModule, TranslateModule];
const components = [LoaderComponent, TimezoneSelectComponent];
const pipes = [AsyncPipe, DatePipe, SlicePipe];

@Component({
  selector: 'app-new-calendar-picker',
  templateUrl: './new-calendar-picker.component.html',
  styleUrls: ['./new-calendar-picker.component.scss'],
  imports: [NgClass, NgStyle, ...modules, ...components, ...pipes]
})
export class NewCalendarPickerComponent implements OnInit {

  public cdref = inject(ChangeDetectorRef);
  private customEventService = inject(CustomEventService);
  private timezoneService = inject(TimezoneService);

  @Input() protected noFreeDates: boolean;
  @Input() protected disablePrev: boolean;
  @Input() protected viewDate: Date;
  @Input() protected excludeWeekDays: number[];
  @Input() protected calendarLoaded = false;
  @Input() protected days: CalendarMonthViewDay[];
  @Input() protected widgetColorConf?: WidgetColorConf;
  @Input() protected worker: UserDao;
  @Input() protected maxCalendarAvailabilityDate: Date;
  @Input() protected lang: string;
  @Input() protected calendarSlotsCountList?: {
    date: Date,
    slotsCount: number
  }[] = [];
  @Input() protected partner: Partner;
  protected readonly widgetDisplayTimezoneSource = WIDGET_DISPLAY_TIMEZONE_SOURCE;
  @Input() protected calendarPreselectedDate: string;
  @Input() protected timezones = new Array<TimezoneDbModel>();
  @Input() protected userState: UserState;

  @Output() protected viewDateChangedEvent = new EventEmitter<{ viewDate: Date, isNext: Boolean }>();
  @Output() protected onDateSelectEvent = new EventEmitter<any>();
  @Output() protected loadCalendarWithAvailabilitiesEvent = new EventEmitter<any>();
  @Output('onTimezoneChange') protected onTimezoneChangeEvent = new EventEmitter<string>();

  protected disableNext = false;
  protected preselectedEventTriggered = false;
  protected weekStartsOn = 1;
  protected refreshCalendar = new Subject<void>();

  ngOnInit(): void {
    this.customEventService.disableCalendarNextButtonEvent.subscribe(isDisableNextButton => {
      setTimeout(() => this.disableNext = isDisableNextButton, 500);
    });

    this.customEventService.refreshCalendarEvent.subscribe(() => this.refreshCalendar.next());

    this.validateMaxDate(this.viewDate);
  }

  protected validateMaxDate(viewDate: Date) {
    this.disableNext = viewDate.getMonth() == this.maxCalendarAvailabilityDate.getMonth() && viewDate.getFullYear() == this.maxCalendarAvailabilityDate.getFullYear();
  }

  protected viewDateChanged(viewDate: Date, isNext: boolean) {
    this.validateMaxDate(viewDate);
    this.viewDate = viewDate;
    this.viewDateChangedEvent.emit({ viewDate: viewDate, isNext: isNext });
  }

  protected onDateSelect(day: any) {
    if (day.day.inMonth) {
      this.onDateSelectEvent.emit(day);
    }
  }

  protected loadCalendarWithAvailabilities(body: any) {
    this.loadCalendarWithAvailabilitiesEvent.emit(body);
  }

  protected getColorOfDay(day: any): any {
    const timezone = this.userState.timezone || this.timezoneService.getLocalTimezone();
    let calendarPreselectedDate: Date;
    let hasAlreadySelected = false;

    if (this.calendarPreselectedDate) {
      calendarPreselectedDate = new Date(moment.tz(this.calendarPreselectedDate, timezone).format(DATE_TIME_ISO_T_FORMAT));
    }

    for (const calendarSlotsCount of this.calendarSlotsCountList) {
      if (day.date.toString() === calendarSlotsCount.date.toString()) {
        if (calendarSlotsCount.slotsCount && calendarSlotsCount.slotsCount > 0) {
          hasAlreadySelected = true;
          day.cssClass = 'cal-selected';
          day.badgeTotal = calendarSlotsCount.slotsCount;
        }
      }
    }

    if (this.calendarLoaded && hasAlreadySelected === false && calendarPreselectedDate) {
      if (day.date.getTime() === calendarPreselectedDate.getTime()) {
        if (
          day.isWeekend === false &&
          day.isPast === false &&
          !this.preselectedEventTriggered
        ) {
          this.preselectedEventTriggered = true;
          day.cssClass = 'cal-selected';
          this.onDateSelect({ day });
        }
      }
    }

    return of({});
  }

  ngAfterViewInit(): void {
    this.cdref.detectChanges();
  }
}
